import React from "react"
import { QualityBadge } from "./DashboardComponents"
import { DefinitionButton } from "./DefinitionExperience"
import { isFrequencyUnit, isImprovement, percentageLabel } from "./formatters"

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return "Unavailable"
  return Number(value).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

// Whole days, or half days for a median between two accounts: "1 day", "18.5 days".
const formatDays = value => (
  `${formatNumber(value, Number.isInteger(value) ? 0 : 1)} ${value === 1 ? "day" : "days"}`
)

const formattedMetricValue = (metric, side) => {
  const value = metric[side].value
  if (isFrequencyUnit(metric.unit)) return formatNumber(value, 2)
  if (metric.unit === "percent") {
    return value === null || value === undefined ? "Unavailable" : `${formatNumber(value, 1)}%`
  }
  if (metric.unit === "days") return Number.isFinite(value) ? formatDays(value) : "Unavailable"
  return formatNumber(value)
}

const metricValue = metric => formattedMetricValue(metric, "current")

const comparisonValue = metric => formattedMetricValue(metric, "comparison")

const signedDays = value => {
  if (value === 0) return formatDays(0)
  return `${value > 0 ? "+" : "−"}${formatDays(Math.abs(value))}`
}

// Rates change in points and times in days; the other metrics in percent.
const metricDelta = metric => {
  if (Number.isFinite(metric.delta?.points)) return signedPercentage(metric.delta.points, " pp")
  if (Number.isFinite(metric.delta?.days)) return signedDays(metric.delta.days)
  return percentageLabel(metric.delta?.percentage)
}

const metricDeltaValue = metric => metric.delta?.points ?? metric.delta?.days ?? metric.delta?.percentage

// An unknown or zero change is neither good nor bad news.
const metricDeltaClass = metric => {
  const value = metricDeltaValue(metric)
  if (!Number.isFinite(value) || value === 0) return ""
  return isImprovement(metric, value) ? "is-positive" : "is-negative"
}

// The API names each denominator; a metric without the name keeps the previous wording.
const denominatorLabel = metric => metric.definition?.denominatorLabel || (
  metric.key === "playerRetention" ? "comparison-period Active Players" : "Event Participants"
)

const signedNumber = value => {
  if (value === null || value === undefined) return "n/a"
  if (value === 0) return "0"
  return `${value > 0 ? "+" : "−"}${formatNumber(Math.abs(value))}`
}

const signedPercentage = (value, suffix = "%") => {
  if (value === null || value === undefined) return "n/a"
  if (value === 0) return `0.0${suffix}`
  return `${value > 0 ? "+" : "−"}${formatNumber(Math.abs(value), 1)}${suffix}`
}

// "250 · 25.0% of 1,000 sign-ups": a count of sign-ups with its share of all the period's sign-ups.
const signupShare = side => {
  if (Number.isFinite(side.share)) {
    return `${formatNumber(side.value)} · ${formatNumber(side.share, 1)}% of ${formatNumber(side.signups)} sign-ups`
  }
  return side.signups === 0 ? "No player sign-ups in this period" : null
}

// "175 of 700 new accounts": the New Accounts behind an activation rate, never beside an Unavailable one.
const activatedAccounts = side => {
  if (Number.isFinite(side.value)) {
    return `${formatNumber(side.numerator)} of ${formatNumber(side.denominator)} new accounts`
  }
  return side.denominator === 0 ? "No new accounts in this period" : null
}

// "20 days from sign-up · 17 days from confirmation": both starting points of a time, never beside an Unavailable one.
const daysFromStarts = (fromSignup, fromConfirmation) => (
  Number.isFinite(fromSignup)
    ? `${formatDays(fromSignup)} from sign-up · ${formatDays(fromConfirmation)} from confirmation`
    : null
)

const medianDays = side => daysFromStarts(side.value, side.fromConfirmation)

const averageDays = side => daysFromStarts(side.average, side.averageFromConfirmation)

// One line per period that has something to say, optionally named ("Median …", "Comparison: median …").
const PeriodLines = ({ metric, describe, name }) => {
  const current = describe(metric.current)
  const comparison = describe(metric.comparison)
  return (
    <>
      {current && <small>{name ? `${name} ${current}` : current}</small>}
      {comparison && <small>Comparison: {name ? `${name.toLowerCase()} ${comparison}` : comparison}</small>}
    </>
  )
}

// The median on the card; the averages, which one long wait can pull far from it, in the details.
const TimeToFirstLines = ({ metric }) => (
  <>
    <PeriodLines metric={metric} describe={medianDays} name="Median" />
    {(averageDays(metric.current) || averageDays(metric.comparison)) && (
      <details className="analytics-kpi-details">
        {/* Both time cards have one: the name says whose averages they are. */}
        <summary aria-label={`Averages for ${metric.label}`}>Averages</summary>
        <PeriodLines metric={metric} describe={averageDays} name="Average" />
      </details>
    )}
  </>
)

const defaultEyebrow = metric => (
  metric.key === "confirmedParticipation" ? "Leading indicator" : "Participation driver"
)

const MetricCard = ({ metric, eyebrow = defaultEyebrow(metric) }) => (
  <article className="analytics-kpi-card analytics-participation-kpi">
    <div className="analytics-kpi-topline">
      <span>{eyebrow}</span>
      <QualityBadge quality={metric.current.quality} />
    </div>
    <h3>{metric.label}</h3>
    <strong className="analytics-kpi-value">{metricValue(metric)}</strong>
    <div className="analytics-kpi-delta">
      <b className={metricDeltaClass(metric)}>
        {metricDelta(metric)}
      </b>
      <span>vs {comparisonValue(metric)}</span>
    </div>
    {metric.current.denominatorValue !== undefined && (
      <small>Denominator: {formatNumber(metric.current.denominatorValue)} {denominatorLabel(metric)}</small>
    )}
    {metric.current.signups !== undefined && <PeriodLines metric={metric} describe={signupShare} />}
    {metric.current.numerator !== undefined && <PeriodLines metric={metric} describe={activatedAccounts} />}
    {metric.unit === "days" && <TimeToFirstLines metric={metric} />}
    <p>{metric.definition.summary}</p>
    <DefinitionButton metric={metric} />
  </article>
)

const ParticipationTrend = ({ chart }) => {
  const points = chart?.points || []
  const maximum = Math.max(
    ...points.flatMap(point => [point.current.value, point.comparison.value]),
    1
  )
  return (
    <section className="analytics-chart-card analytics-participation-trend" aria-labelledby="participation-trend-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Aligned period trend</p>
          <h2 id="participation-trend-title">Confirmed Participation</h2>
          <p>Current and Comparison use equivalent elapsed coverage in every bucket.</p>
        </div>
        <span className="analytics-chart-legend"><i /> Current <i /> Comparison</span>
      </div>
      <div
        className="analytics-bucket-plot"
        role="img"
        aria-label={chart.accessibleSummary}
        style={{ "--analytics-bucket-count": points.length }}
      >
        {points.map(point => (
          <div className="analytics-bucket" key={point.key}>
            <div className="analytics-bucket-bars" aria-hidden="true">
              <b style={{ height: `${(point.current.value / maximum) * 100}%` }} />
              <b style={{ height: `${(point.comparison.value / maximum) * 100}%` }} />
            </div>
            <strong>{point.label}</strong>
            <span className={(point.delta.absolute || 0) >= 0 ? "is-positive" : "is-negative"}>
              {signedNumber(point.delta.absolute)}
            </span>
          </div>
        ))}
      </div>
      <div className="analytics-table-wrap">
        <table className="analytics-data-table analytics-trend-values" aria-label="Confirmed Participation aligned values">
          <thead>
            <tr><th>Bucket</th><th>Current</th><th>Comparison</th><th>Difference</th><th>Difference %</th></tr>
          </thead>
          <tbody>
            {points.map(point => (
              <tr key={point.key}>
                <th>{point.label}</th>
                <td><span className="sr-only">Current </span>{formatNumber(point.current.value)}</td>
                <td><span className="sr-only">Comparison </span>{formatNumber(point.comparison.value)}</td>
                <td className={(point.delta.absolute || 0) >= 0 ? "is-positive" : "is-negative"}>{signedNumber(point.delta.absolute)}</td>
                <td>{signedPercentage(point.delta.percentage)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

const BreakdownTable = ({ breakdown }) => (
  <div className="analytics-table-wrap">
    <table className="analytics-data-table" aria-label={`Entries Breakdown — ${breakdown.label}`}>
      <thead>
        <tr>
          <th>{breakdown.label}</th><th>Current</th><th>Comparison</th><th>Δ</th><th>Δ%</th><th>Share</th><th>Share change</th>
        </tr>
      </thead>
      <tbody>
        {breakdown.rows.map(row => (
          <tr key={row.key}>
            <th>{row.label}</th>
            <td>{formatNumber(row.current)}</td>
            <td>{formatNumber(row.comparison)}</td>
            <td className={(row.delta.absolute || 0) >= 0 ? "is-positive" : "is-negative"}>{signedNumber(row.delta.absolute)}</td>
            <td>{signedPercentage(row.delta.percentage)}</td>
            <td>{formatNumber(row.share.current, 1)}%</td>
            <td>{signedPercentage(row.share.changePoints, " pp")}</td>
          </tr>
        ))}
        <tr className="analytics-total-row">
          <th>Total</th>
          <td>{formatNumber(breakdown.reconciliation.current.actual)}</td>
          <td>{formatNumber(breakdown.reconciliation.comparison.actual)}</td>
          <td>{signedNumber(
            breakdown.reconciliation.current.actual - breakdown.reconciliation.comparison.actual
          )}</td>
          <td colSpan="3">100.0% of {breakdown.denominator.label}</td>
        </tr>
      </tbody>
    </table>
  </div>
)

const MatrixTable = ({ breakdown }) => (
  <div className="analytics-table-wrap">
    <table className="analytics-data-table analytics-matrix-table" aria-label={`Entries Breakdown — ${breakdown.label}`}>
      <thead>
        <tr>
          <th rowSpan="2">Game Format</th>
          {breakdown.columns.map(column => <th colSpan="2" key={column.key}>{column.label}</th>)}
          <th colSpan="2">Total</th>
        </tr>
        <tr>
          {breakdown.columns.flatMap(column => [
            <th key={`${column.key}-current`}>Current</th>,
            <th key={`${column.key}-comparison`}>Comparison</th>,
          ])}
          <th>Current</th><th>Comparison</th>
        </tr>
      </thead>
      <tbody>
        {breakdown.rows.map(row => (
          <tr key={row.key}>
            <th>{row.label}</th>
            {row.cells.flatMap(cell => [
              <td key={`${cell.key}-current`}>{formatNumber(cell.current)}</td>,
              <td key={`${cell.key}-comparison`}>{formatNumber(cell.comparison)}</td>,
            ])}
            <td>{formatNumber(row.current)}</td><td>{formatNumber(row.comparison)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

// Fewer withdrawals is good news; an unknown or zero change gets no colour.
const rateChangeClass = points => {
  if (!Number.isFinite(points) || points === 0) return ""
  return points < 0 ? "is-positive" : "is-negative"
}

// An Unavailable rate's counts can leave withdrawals out (with Activity Logs unread, older
// withdrawals all look undated), so none of them is shown.
const isUnavailableRate = side => side.value === null || side.quality?.state === "unavailable"

const rateSlots = side => (isUnavailableRate(side)
  ? null
  : (
    <small>
      {formatNumber(side.numerator)} of {formatNumber(side.denominator)} Participant Slots (Confirmed Participation + player withdrawals after entries close)
    </small>
  ))

// Only the rate's own row says whether things got better: more player withdrawals after close is
// bad news, fewer is good, no change is neither. The other rows are context and stay neutral.
const breakdownDeltaClass = row => {
  if (!row.includedInRate || !Number.isFinite(row.delta) || row.delta === 0) return ""
  return row.delta > 0 ? "is-negative" : "is-positive"
}

const WithdrawalGuardrail = ({ guardrail }) => {
  if (!guardrail) return null
  const qualityRank = { trusted: 0, estimated: 1, partial: 2, unavailable: 3 }
  const displayedQuality = [guardrail.current.quality, guardrail.comparison.quality]
    .sort((left, right) => qualityRank[right.state] - qualityRank[left.state])[0]
  const currentRate = guardrail.current.value === null
    ? "Unavailable"
    : `${formatNumber(guardrail.current.value, 1)}%`
  const comparisonRate = guardrail.comparison.value === null
    ? "Unavailable"
    : `${formatNumber(guardrail.comparison.value, 1)}%`
  const hidesCurrent = isUnavailableRate(guardrail.current)
  const hidesComparison = isUnavailableRate(guardrail.comparison)
  return (
    <section className="analytics-withdrawal-section" aria-labelledby="withdrawal-guardrail-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Demand leakage</p>
          <h2 id="withdrawal-guardrail-title">{guardrail.label}</h2>
          <p>Only withdrawals made by players at or after the event's entries close count in the rate; the other rows are context.</p>
        </div>
        <QualityBadge quality={displayedQuality} />
      </div>
      <div className="analytics-withdrawal-summary">
        <article>
          <span>Current rate</span>
          <strong>{currentRate}</strong>
          {rateSlots(guardrail.current)}
        </article>
        <article>
          <span>Comparison rate</span>
          <strong>{comparisonRate}</strong>
          {rateSlots(guardrail.comparison)}
        </article>
        <article>
          <span>Rate change</span>
          <strong className={rateChangeClass(guardrail.delta.points)}>
            {signedPercentage(guardrail.delta.points, " pp")}
          </strong>
          <small>Percentage-point movement</small>
        </article>
        <article>
          <span>Evidence coverage</span>
          <strong>{formatNumber(guardrail.current.actorCoverage, 1)}% evidence coverage</strong>
          <small>Comparison {formatNumber(guardrail.comparison.actorCoverage, 1)}%</small>
        </article>
      </div>
      <div className="analytics-withdrawal-quality">
        <p><strong>Current:</strong> {guardrail.current.quality.reason}</p>
        <p><strong>Comparison:</strong> {guardrail.comparison.quality.reason}</p>
      </div>
      <div className="analytics-table-wrap">
        <table className="analytics-data-table analytics-withdrawal-table" aria-label="Withdrawal breakdown">
          <thead>
            <tr><th>Withdrawals</th><th>Current</th><th>Comparison</th><th>Δ</th><th>Rate population</th></tr>
          </thead>
          <tbody>
            {guardrail.breakdown.map(row => (
              <tr key={row.key} className={`analytics-withdrawal-${row.key}`}>
                <th>{row.label}</th>
                <td>{hidesCurrent ? "—" : formatNumber(row.current)}</td>
                <td>{hidesComparison ? "—" : formatNumber(row.comparison)}</td>
                {hidesCurrent || hidesComparison
                  ? <td>—</td>
                  : <td className={breakdownDeltaClass(row)}>{signedNumber(row.delta)}</td>}
                <td>{row.includedInRate ? "Included" : "Excluded"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(hidesCurrent || hidesComparison) && (
        <p className="analytics-withdrawal-note">— marks the counts of a period whose rate is Unavailable; its reason is given above.</p>
      )}
      <aside className="analytics-definition-note analytics-withdrawal-definition">
        <strong>How this is calculated</strong>
        <p>{guardrail.definition.summary}</p>
        <p>{guardrail.definition.formula}</p>
        <p><b>Excluded:</b> {guardrail.definition.exclusions} <b>Business Date:</b> {guardrail.definition.businessDate}</p>
      </aside>
    </section>
  )
}

const EntriesBreakdown = ({ breakdowns, selectedKey, onSelectedKeyChange }) => {
  const availableBreakdowns = breakdowns || {}
  const keys = Object.keys(availableBreakdowns)
  const effectiveKey = availableBreakdowns[selectedKey] ? selectedKey : keys[0]
  const selected = availableBreakdowns[effectiveKey]
  if (!selected) return null
  return (
    <section className="analytics-breakdown-section" aria-labelledby="entries-breakdown-title">
      <div className="analytics-section-heading analytics-breakdown-heading">
        <div>
          <p className="analytics-eyebrow">Participant Slot mix</p>
          <h2 id="entries-breakdown-title">Entries Breakdown</h2>
          <p>Each dimension reconciles independently to Confirmed Participation.</p>
        </div>
        <QualityBadge quality={selected.quality} />
      </div>
      <div className="analytics-segment-controls" aria-label="Entries breakdown dimension">
        {keys.map(key => (
          <button
            type="button"
            key={key}
            aria-pressed={effectiveKey === key}
            onClick={() => onSelectedKeyChange(key)}
          >
            {availableBreakdowns[key].label}
          </button>
        ))}
      </div>
      {selected.columns ? <MatrixTable breakdown={selected} /> : <BreakdownTable breakdown={selected} />}
      <p className="analytics-reconciliation-note">
        <strong>{selected.quality.state === "trusted" ? "Reconciled" : "Check reconciliation"}:</strong>{" "}
        Current discrepancy {signedNumber(selected.reconciliation.current.discrepancy)} · Comparison discrepancy {signedNumber(selected.reconciliation.comparison.discrepancy)}.
      </p>
    </section>
  )
}

// The account acquisition metrics, shown apart from the event participation indicators.
const NEW_ACCOUNT_METRICS = new Set([
  "newAccounts",
  "unconfirmedSignups",
  "eventActivation",
  "activityActivation",
  "timeToFirstEvent",
  "timeToFirstActivity",
])

const NewAccounts = ({ metrics }) => {
  if (!metrics.length) return null
  return (
    <section className="analytics-new-accounts" aria-labelledby="new-accounts-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Account acquisition</p>
          <h2 id="new-accounts-title">New accounts</h2>
          <p>Player accounts created in the period, by their home location. The sport filter does not change the account counts; with a sport selected, activation and the time to a first event or activity count only that sport's activity, wherever it took place.</p>
        </div>
      </div>
      <section className="analytics-participation-kpis" aria-label="New account indicators">
        {metrics.map(metric => <MetricCard metric={metric} eyebrow="Account acquisition" key={metric.key} />)}
      </section>
    </section>
  )
}

const ParticipationView = ({ snapshot, selectedBreakdownKey, onSelectedBreakdownChange }) => {
  const chart = snapshot.charts?.find(({ key }) => key === "confirmedParticipationTrend")
  const indicators = snapshot.kpis.filter(metric => !NEW_ACCOUNT_METRICS.has(metric.key))
  const newAccountMetrics = snapshot.kpis.filter(metric => NEW_ACCOUNT_METRICS.has(metric.key))
  return (
    <section className="analytics-participation-view" aria-labelledby="participation-title">
      <div className="analytics-view-heading">
        <div>
          <p className="analytics-eyebrow">Participation perspective</p>
          <h2 id="participation-title">Event participation mechanics</h2>
          <p>Event demand plus cross-activity Active Players and churn, with event-only frequency, churn and mix kept explicit.</p>
        </div>
      </div>
      <section className="analytics-participation-kpis" aria-label="Participation indicators">
        {indicators.map(metric => <MetricCard metric={metric} key={metric.key} />)}
      </section>
      <NewAccounts metrics={newAccountMetrics} />
      {chart && <ParticipationTrend chart={chart} />}
      <WithdrawalGuardrail guardrail={snapshot.guardrails?.withdrawalRate} />
      <EntriesBreakdown
        breakdowns={snapshot.breakdowns}
        selectedKey={selectedBreakdownKey}
        onSelectedKeyChange={onSelectedBreakdownChange}
      />
      <aside className="analytics-definition-note">
        <strong>Definition reference</strong>
        <p>One participation is one person in one competition. A complete doubles team counts two; only demonstrable players are counted. Waiting and pre-registration are excluded; withdrawals are measured separately in the guardrail above.</p>
        <p>Game Format and Competition Structure are local segmentations. Premium / Free membership is not Paid / Free Entry.</p>
      </aside>
    </section>
  )
}

export default ParticipationView
